<script setup>

import dayjs from "dayjs";
import Card from "@/Components/Card.vue";
import {router, usePage} from "@inertiajs/vue3";
import StarReview from "@/Components/Icons/StarReview.vue";
import {computed, ref, watch} from "vue";
import debounce from "lodash-es/debounce.js";
import Create from "@/Components/Icons/Create.vue";
import ButtonUnderline from "@/Components/Buttons/ButtonUnderline.vue";

const props = defineProps({
    filter: Object,
})

const emits = defineEmits(['updateCountReview'])

const getStyleStarReview = (rating) => {
    return rating <= 2 ? 'fill-red-500' : rating <= 3 ? 'fill-orange-500' : rating <= 4 ? 'fill-yellow-400' : 'fill-green-500'
}
const page = usePage(),
    user = page.props.user,
    reviews = ref(page.props.reviews.data),
    currentPage = ref(1),
    isLoading = ref(false)

const getQueryParams = (nextPage = false) => {
    return {
        review_rating: props.filter.val ? props.filter.val : undefined,
        reviews_page: (nextPage && currentPage.value) ? currentPage.value : undefined
    }
}
const fetch = (nextPage = false) => {
    if (!nextPage) {
        currentPage.value = 1
    }
    isLoading.value = true
    const queryParams = getQueryParams(nextPage)
    router.get(page.url, queryParams,
        {
            preserveState: true,
            preserveScroll: true,
            only: ['reviews'],
            onFinish: () => {
                if (nextPage) {
                    reviews.value = [...reviews.value, ...page.props.reviews.data]
                } else {
                    reviews.value = page.props.reviews.data
                }
                emits('updateCountReview');
                isLoading.value = false
            }
        })
}
const fetchDebounced = debounce(fetch, 1000)
const hasMore = computed(() => {
    return currentPage.value < page.props.reviews.last_page
})
const onShowMore = () => {
    currentPage.value++
    fetch(true)
}

watch(() => props.filter, () => {
    fetch(false)
})
</script>

<template>
    <Card class="flex flex-col pt-1 pb-3" v-if="page.props.reviews.total > 0">
        <template v-for="review in reviews">
            <div class="py-2 border-b border-dashed">
                <div class="px-3 flex justify-between items-center">
                    <div class="flex gap-1">
                        <template v-for="value in review.rating">
                            <StarReview :class="getStyleStarReview(review.rating)"
                                        class="w-2.5 h-2.5"/>
                        </template>
                    </div>
                    <div class="text-sm text-slate-500 dark:text-slate-400 font-normal">{{ dayjs(review.created_at).fromNow }}
                    </div>
                </div>
                <div class="px-3 py-1.5 text-xs dark:text-slate-200 font-medium leading-full">
                    {{ review.title }}
                </div>
                <div class="px-2 flex flex-col gap-2">
                    <span class="text-sm dark:text-slate-200 tracking-wider leading-full">{{ review.text }}</span>
                    <template v-if="review.review_answer">
                        <div
                            class="bg-slate-100 dark:bg-deep-blue p-2 rounded-lg text-sm leading-120 tracking-wider flex flex-col gap-0.5">
                            <div class="flex"><span class="text-slate-500 dark:text-slate-400">{{ $t('Seller`s response') }}: </span>
                                <span class="dark:text-slate-200">{{ review.review_answer.text }}</span></div>
                            <ButtonUnderline v-if="user.id === page.props.auth.user?.id && review.review_answer"
                                             :text="$t('Edit answer')">
                                <template #icon>
                                    <Create class="group-hover:stroke-black dark:group-hover:stroke-slate-200 transition-colors"/>
                                </template>
                            </ButtonUnderline>
                        </div>
                    </template>
                </div>
                <template v-if="user.id === page.props.auth.user?.id && review.can_answer_review">
                    <div class="flex justify-end px-4 pb-2 leading-full items-center">
                        <ion-icon name="chatbubble-ellipses" class="text-xl pl-2 pr-1 align-middle fill-slate-500 dark:fill-slate-400" style="width: 20px; height: 20px"></ion-icon>
                        <span class="text-xs leading-full underline">{{ $t('Write answer') }}</span>
                    </div>
                </template>
            </div>
        </template>
        <div v-if="hasMore" class="border-t border-dashed px-2.5 pt-1.5">
            <div
                @click="onShowMore"
                class="cursor-pointer border dark:text-slate-200 grow flex items-center justify-center border-slate-500 dark:border-slate-400 rounded-lg w-full h-10 transition-colors hover:bg-slate-200 dark:hover:bg-slate-800 text-sm font-normal">
                <span>{{ $t('Show more') }}</span>
            </div>
        </div>
    </Card>
    <Card v-else class="px-3 py-5">
        <div class="px-2.5 dark:text-slate-200 text-sm font-semibold leading-120">
            {{ $t('No reviews yet.') }}
        </div>
    </Card>
</template>

<style scoped>

</style>
